/* eslint-disable no-console */
import React, { useState, useEffect, useMemo } from "react";
import { Box, Divider, Grid, Typography, Stack } from "@mui/material";
import BasicSelect from "../../components/BasicSelect";
import { useGroupLabels } from "../../hooks/groupLabels";
import { useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../stores/user.store";
import { Program } from "../../domain/beneficiary.interface";
import { useGetOrganizationList } from "../../hooks/useOrganizations";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import {
	useGetProgramsByIds,
	useGetProviderSitesByOUId,
	useGetProvidersByProgramId,
} from "../../hooks/useBeneficiaries";

interface Filters {
	programId: string;
	providerId: string;
	providerSiteId: string;
	search: string;
	pageNumber: number;
	pageSize: number;
	dateRange: "lastMonth" | "lastQuarter" | "lastYear";
	zipCode: string;
}

interface HeaderProps {
	filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
	handleSearch?: () => void;
	// SIMS-7002: opt-out for callers that want the date-range select hidden
	// (B2D dashboard). Defaults to true so the legacy B2C dashboard keeps
	// rendering the filter unchanged.
	showDateRangeFilter?: boolean;
}

const Header: React.FC<HeaderProps> = ({
	filtersState,
	handleSearch,
	showDateRangeFilter = true,
}) => {
	// Handle OU dropdown change
	const handleProviderSitesChange = (value: string | string[]) => {
		let selected = Array.isArray(value) ? value : [value];

		if (selected.length === 0 && providerSitesOptions.length > 0) {
			selected = providerSitesOptions.map((ou) => ou.id);
		}
		setSelectedProviderSites(selected);
		setSelectedGroupLabels([]); // Deselect group labels when OU is selected

		queryClient.removeQueries({
			queryKey: ["sims-beneficiaries-providers"],
		});

		setFilters((filters) => ({
			...filters,
			providerSiteId: selected.join(","),
			groupLabelIds: [],
			providerId: "",
			pageNumber: 0,
		}));
	};

	const queryClient = useQueryClient();
	const { userInfo } = useUserStore();
	const [filters, setFilters] = filtersState;
	const [selectedProviderSites, setSelectedProviderSites] = useState<string[]>(
		[],
	);
	const [providerSitesOptions, setProviderSitesOptions] = useState<
		ProviderSiteWithLabels[]
	>([]);

	const [showFilters] = useState(true);
	const createAuditLog = useCreateAuditlog();

	const [programsOptions, setProgramsOptions] = useState<
		{ key: string; value: string }[]
	>([]);
	const [providerOptions, setProviderOptions] = useState<
		{ key: string; value: string }[]
	>([]);
	type ProviderSiteWithLabels = {
		key: string;
		id: string;
		value: string;
		name: string;
		groupLabelIds?: string[];
	};

	const [selectedProgram, setSelectedProgram] = useState<Program | undefined>();
	const [, setUserProgram] = useState<Program | undefined>();
	const [selectedGroupLabels, setSelectedGroupLabels] = useState<string[]>([]);
	const { data: groupLabels = [], isLoading: isLoadingGroupLabels } =
		useGroupLabels();

	const { data: dataOrganization } = useGetOrganizationList(
		userInfo?.accountId as string,
	);

	const organizationsAssigned = useMemo(() => {
		const userOrganizations = userInfo?.programs.flatMap((program) =>
			program.orgUnitId.map((o) => (typeof o === "string" ? o : o.orgUnitId)),
		);

		return (
			dataOrganization?.filter((org) => userOrganizations?.includes(org.id)) ||
			[]
		);
	}, [userInfo, dataOrganization]);

	const programIds = useMemo(
		() => userInfo?.programs.map((p) => p.programId),
		[userInfo],
	);

	// Fetch program metadata
	const { data: programs } = useGetProgramsByIds(programIds || []);

	// Fetch OU tree whenever program selection changes
	const {
		data: providerSites,
		refetch: refetchProviderSites,
		isFetching: isProviderSitesFetching,
	} = useGetProviderSitesByOUId({
		ouIds:
			userInfo?.programs
				.filter((p) => p.programId === filters.programId)
				.flatMap((p) =>
					p.orgUnitId.map((o) => (typeof o === "string" ? o : o.orgUnitId)),
				) || [],
		queryKey: "sims-beneficiaries-provider-sites",
	}) as {
		data: ProviderSiteWithLabels[];
		refetch: () => void;
		isFetching: boolean;
	};

	// Fetch providers when programId or OU changes
	const {
		data: providers,
		refetch: refetchProviders,
		isFetching: isProvidersFetching,
	} = useGetProvidersByProgramId(
		filters.programId ? [filters.programId] : [],
		filters.providerSiteId
			? filters.providerSiteId.split(",").filter(Boolean)
			: [],
		true,
	);

	useEffect(() => {
		if (filters.programId && filters.providerSiteId && handleSearch) {
			handleSearch();

			createAuditLog.mutateAsync({
				appType: "WEB_BACK_OFFICE",
				module: "Dashboard",
				action: "When filtering",
				detail: "Filtered dashboard",
				actionCode: "WEB_DASH_FILTERED",
				option: "Dashboard",
				createdBy: userInfo?.id as string,
			});
		}
	}, [filters.programId, handleSearch]);

	useEffect(() => {
		if (programs?.length) {
			setProgramsOptions([
				...programs.map((p) => ({ key: p._id, value: p.name })),
			]);
			if (filters.programId === "") {
				setFilters((prev) => ({
					...prev,
					programId: programs[0]._id,
				}));
				setUserProgram(programs[0] as unknown as Program);
				setSelectedProgram(programs[0] as unknown as Program);

				queryClient.removeQueries({
					queryKey: ["sims-beneficiaries-providers"],
				});

				refetchProviderSites();
			} else {
				// Set selected program based on current filter
				const currentProgram = programs.find(
					(p) => p._id === filters.programId,
				);
				if (currentProgram) {
					setSelectedProgram(currentProgram as unknown as Program);
				}
			}
		}
	}, [programs, queryClient, refetchProviderSites, filters.programId]);

	useEffect(() => {
		if (providerSites) {
			setProviderSitesOptions(providerSites);
		}
	}, [providerSites]);

	useEffect(() => {
		const allOuIds = providerSitesOptions.map((ou) => ou.id);

		const validSelected = selectedProviderSites.filter((id) =>
			allOuIds.includes(id),
		);
		if (allOuIds.length > 0 && validSelected.length === 0) {
			setSelectedProviderSites(allOuIds);
			setFilters((filters) => ({
				...filters,
				providerSiteId: allOuIds.join(","),
			}));
		}
	}, [providerSitesOptions]);

	// Filter providers by CM roles
	const filteredProviders = useMemo(
		() =>
			providers
				? providers
						.filter((p) =>
							p.roles?.some((r) => selectedProgram?.cmRoles?.includes(r)),
						)
						.sort((a, b) => {
							if (a.value === "All") return -1;
							if (b.value === "All") return 1;
							return a.value.localeCompare(b.value);
						})
				: [],
		[providers, selectedProgram],
	);

	useEffect(() => {
		setProviderOptions(filteredProviders);
	}, [filteredProviders]);

	// Refetch provider sites when program changes
	useEffect(() => {
		if (filters.programId !== "") {
			refetchProviderSites();
		}
	}, [filters.programId, refetchProviderSites]);

	// Refetch providers only when both program AND provider sites are available
	useEffect(() => {
		if (filters.programId !== "" && filters.providerSiteId) {
			refetchProviders().catch((error) => {
				console.error("Error al cargar providers:", error);
			});
		}
	}, [filters.programId, filters.providerSiteId, refetchProviders]);

	// Handle Group Label dropdown change
	const handleGroupLabelsChange = (value: string | string[]) => {
		const selectedLabels = Array.isArray(value) ? value : [value];
		setSelectedGroupLabels(selectedLabels);

		let selectedOuIds: string[];
		if (selectedLabels.length === 0) {
			// No labels selected: select all OUs
			selectedOuIds = providerSitesOptions.map((ou) => ou.id);
		} else {
			// Find OUs with any of the selected group labels
			const matchingOUs = (providerSitesOptions || []).filter(
				(ou) =>
					Array.isArray(ou.groupLabelIds) &&
					ou.groupLabelIds.some((labelId) => selectedLabels.includes(labelId)),
			);
			selectedOuIds = matchingOUs.map((ou) => ou.id);
		}

		setSelectedProviderSites(selectedOuIds);
		setFilters((filters) => ({
			...filters,
			groupLabelIds: selectedLabels,
			providerSiteId: selectedOuIds.join(","),
			providerId: "",
			pageNumber: 0,
		}));
	};

	const groupLabelOptions = useMemo(() => {
		const availableLabels = providerSites?.flatMap(
			(ou) => ou.groupLabelIds || [],
		);
		return groupLabels
			.filter((l) => availableLabels?.includes(l._id))
			.map((label) => ({
				key: label._id,
				value: label.name,
			}));
	}, [groupLabels, providerSites]);

	return (
		<Box marginBottom={2}>
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={12} md={6}>
					<Stack direction="column">
						<Typography variant="h4" fontWeight={700} gutterBottom>
							Hello, {userInfo?.firstName} {userInfo?.lastName}!
						</Typography>
						<Typography
							variant="titleSmall"
							color={(theme) => theme.palette.textMain.main2}
						>
							{organizationsAssigned.map((org) => org.name).join(", ")}
						</Typography>
						<Typography
							variant="bodyLarge"
							color={(theme) => theme.palette.textMain.main2}
						>
							Here is what is going on today.
						</Typography>
					</Stack>
				</Grid>
				{handleSearch && (
					<Grid
						item
						xs={12}
						md={6}
						container
						spacing={2}
						justifyContent="flex-end"
					>
						<Grid item xs={6}>
							<BasicSelect
								sx={{
									backgroundColor: "white",
								}}
								handleChange={(value) => {
									queryClient.removeQueries({
										queryKey: ["sims-beneficiaries-provider-sites"],
									});

									queryClient.removeQueries({
										queryKey: ["sims-beneficiaries-providers"],
									});

									setSelectedProviderSites([]);

									setFilters((filters) => ({
										...filters,
										programId: value as string,
										providerSiteId: "",
										providerId: "",
										pageNumber: 0,
									}));

									setSelectedGroupLabels([]);

									const newSelectedProgram = programs?.find(
										(p) => p._id === value,
									);
									setSelectedProgram(newSelectedProgram as unknown as Program);

									setUserProgram(
										userInfo?.programs?.find(
											(program) => program.programId === value,
										) as unknown as Program,
									);

									if (value === "all") {
										setUserProgram(undefined);
										setSelectedProgram(undefined);
									}
								}}
								data={programsOptions || []}
								value={filters.programId}
								label="Filter by program"
								id="beneficiary-program"
								isLoading={isProviderSitesFetching}
							/>
						</Grid>
					</Grid>
				)}
			</Grid>
			{showFilters && (
				<Box marginTop={2} marginBottom={2}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6} md={3}>
							<BasicSelect
								sx={{ backgroundColor: "white" }}
								handleChange={handleProviderSitesChange}
								data={(providerSitesOptions || []).map((ou) => ({
									key: ou.id || ou.key,
									value: ou.name || ou.value,
								}))}
								value={selectedProviderSites}
								label="Filter by OU"
								id="dashboard-ou"
								multiple
								isLoading={isProviderSitesFetching}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<BasicSelect
								sx={{ backgroundColor: "white" }}
								handleChange={handleGroupLabelsChange}
								data={groupLabelOptions}
								value={selectedGroupLabels}
								label="Filter by Group Label"
								id="dashboard-group-label"
								multiple
								isLoading={isLoadingGroupLabels}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<BasicSelect
								sx={{
									backgroundColor: "white",
								}}
								handleChange={(value) => {
									setFilters((filters) => ({
										...filters,
										providerId: value as string,
										pageNumber: 0,
									}));
								}}
								data={providerOptions || []}
								value={filters.providerId}
								isLoading={isProvidersFetching}
								disabled={!filters.providerSiteId || isProvidersFetching}
								label="Filter by provider"
								id="beneficiary-provider"
							/>
						</Grid>
						{/* <Grid item xs={12} sm={6} md={3}>
							<TextField
								sx={{
									backgroundColor: "white",
								}}
								fullWidth
								label="Zip code"
								value={filters.zipCode}
								onChange={(e) => {
									const newZipCode = e.target.value.trim();
									setFilters((f) => ({
										...f,
										zipCode: newZipCode,
										pageNumber: 0,
									}));
								}}
								inputProps={{ maxLength: 10 }}
								placeholder="e.g. 90210"
								variant="outlined"
							/>
						</Grid> */}
						{showDateRangeFilter && (
							<Grid item xs={12} sm={6} md={3}>
								<BasicSelect
									sx={{
										backgroundColor: "white",
									}}
									label="Date range"
									data={[
										{ key: "lastMonth", value: "Last month" },
										{ key: "lastQuarter", value: "Last quarter" },
										{ key: "lastYear", value: "Last year" },
									]}
									value={filters.dateRange}
									handleChange={(rangeKey) => {
										// BasicSelect surfaces `string | string[]`; narrow to the
										// literal union the Filters shape expects so we drop the
										// previous `as any` cast.
										if (
											rangeKey === "lastMonth" ||
											rangeKey === "lastQuarter" ||
											rangeKey === "lastYear"
										) {
											setFilters((f) => ({ ...f, dateRange: rangeKey }));
										}
									}}
									id="date-range-filter"
								/>
							</Grid>
						)}
					</Grid>
				</Box>
			)}
			<div className="py-5">
				<Divider
					sx={{
						bgcolor: "surface.main",
						borderWidth: 2,
						position: "absolute",
						left: 0,
						right: 0,
					}}
				/>
			</div>
		</Box>
	);
};

export default Header;
